import { forwardRef, useImperativeHandle, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Dropdown, Button, Typography, Space, Input, InputNumber, Select, Switch, message, Form } from 'antd';
import { PlusOutlined, DeleteOutlined, DownOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  useForm,
  FormProvider,
  useFormContext,
  useFieldArray,
  useWatch,
  Controller,
} from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormField } from '../../shared/form';
import { authFetch } from '../../utils/api';
import {
  NodeProtocolsDeliverySchema,
  NODE_PROTOCOL_TYPE_OPTIONS,
  makeDefaultProtocol,
  omitIncompleteHysteria2ProductionProof,
  type NodeProtocol,
  type NodeProtocolType,
} from '../../schemas/node';

/**
 * Protocols sub-form schema（獨立於主節點表單，由父層透過 ref 收割）。
 */
const ProtocolsFormSchema = z.object({
  protocols: NodeProtocolsDeliverySchema,
});

type ProtocolsFormValues = z.infer<typeof ProtocolsFormSchema>;

export interface ProtocolsEditorHandle {
  /** 驗證並回傳 protocols；若驗證失敗 reject */
  validateAndGet: () => Promise<NodeProtocol[]>;
  /** 重設為指定 protocols（編輯模式開啟時呼叫） */
  reset: (protocols: NodeProtocol[]) => void;
}

interface Props {
  initialProtocols?: NodeProtocol[];
}

/**
 * SK6 節點「動態協議 fallback」編輯器。
 *
 * 以獨立 RHF FormProvider 管理 `protocols` 陣列 + zodResolver 驗證，父層透過 ref 取得結果。
 * Row 以 `useFieldArray` 實作，新增按鈕為下拉選擇協議類型（trojan / hysteria2 / vless-reality）。
 */
export const ProtocolsEditor = forwardRef<ProtocolsEditorHandle, Props>(
  ({ initialProtocols }, ref) => {
    const form = useForm<ProtocolsFormValues>({
      resolver: zodResolver(ProtocolsFormSchema),
      mode: 'onBlur',
      defaultValues: { protocols: initialProtocols ?? [] },
    });

    useEffect(() => {
      form.reset({ protocols: initialProtocols ?? [] });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialProtocols]);

    useImperativeHandle(
      ref,
      () => ({
        validateAndGet: async () => {
          const ok = await form.trigger();
          if (!ok) {
            throw new Error('protocols validation failed');
          }
          const protocols = omitIncompleteHysteria2ProductionProof(
            ProtocolsFormSchema.parse(form.getValues()).protocols ?? []
          );
          return protocols;
        },
        reset: (protocols) => {
          form.reset({ protocols });
        },
      }),
      [form]
    );

    return (
      <FormProvider {...form}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
          動態 fallback 協議陣列會寫入節點 config 的 <code>protocols</code> 欄位，
          客戶端依此陣列動態生成 sing-box outbound 加入 urltest。空陣列表示不啟用。
        </Typography.Paragraph>
        <ProtocolRowList />
      </FormProvider>
    );
  }
);

ProtocolsEditor.displayName = 'ProtocolsEditor';

// ---------- Row list ----------

const ProtocolRowList: React.FC = () => {
  const { control } = useFormContext<ProtocolsFormValues>();
  const { fields, append, remove } = useFieldArray<ProtocolsFormValues>({
    control,
    name: 'protocols',
  });

  const handleAdd = (type: NodeProtocolType) => {
    append(makeDefaultProtocol(type));
  };

  return (
    <div>
      {fields.length === 0 && (
        <div
          style={{
            padding: '16px',
            textAlign: 'center',
            color: 'rgba(0,0,0,0.45)',
            fontSize: 13,
            border: '1px dashed var(--ant-color-border-secondary, #f0f0f0)',
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          尚未設定任何 fallback 協議
        </div>
      )}
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {fields.map((field, index) => (
          <div
            key={field.id}
            style={{
              position: 'relative',
              padding: '12px 40px 4px 12px',
              border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
              borderRadius: 6,
            }}
          >
            <Button
              type="text"
              size="small"
              danger
              aria-label={`刪除第 ${index + 1} 個協議`}
              icon={<DeleteOutlined />}
              style={{ position: 'absolute', top: 4, right: 4 }}
              onClick={() => remove(index)}
            />
            <ProtocolRow index={index} />
          </div>
        ))}
      </Space>
      <Dropdown
        menu={{
          items: NODE_PROTOCOL_TYPE_OPTIONS.map((opt) => ({
            key: String(opt.value),
            label: opt.label,
            onClick: () => handleAdd(opt.value),
          })),
        }}
      >
        <Button type="dashed" icon={<PlusOutlined />} style={{ width: '100%', marginTop: 12 }}>
          新增協議 <DownOutlined />
        </Button>
      </Dropdown>
    </div>
  );
};

// ---------- Single row ----------

interface ProtocolRowProps {
  index: number;
}

const ProtocolRow: React.FC<ProtocolRowProps> = ({ index }) => {
  const { control } = useFormContext<ProtocolsFormValues>();
  const type = useWatch({
    control,
    name: `protocols.${index}.type` as const,
  }) as NodeProtocolType | undefined;

  const historicalProof = useWatch({ control, name: `protocols.${index}.productionProof` as const });

  const typeLabel = useMemo(
    () =>
      NODE_PROTOCOL_TYPE_OPTIONS.find((opt) => opt.value === type)?.label ??
      String(type ?? ''),
    [type]
  );

  return (
    <div>
      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)', marginBottom: 8 }}>
        協議類型：<strong>{typeLabel}</strong>
      </div>
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.tag` as const}
        label="Tag"
        required
        control={<Input placeholder="例如 trojan、reality" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.priority` as const}
        label="Fallback 優先級"
        control={<InputNumber min={1} max={99} style={{ width: '100%' }} />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.port` as const}
        label="Port"
        required
        control={<InputNumber min={1} max={65535} style={{ width: '100%' }} />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.server` as const}
        label="公開端點"
        control={<Input placeholder="未填則使用節點 IP；origin-hidden 建議必填" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.sni` as const}
        label="SNI"
        required
        control={<Input placeholder="例如 endpoint.swiftmesh.net" />}
      />
      {(type === 'trojan' || type === 'hysteria2') && (
        <FormField<ProtocolsFormValues>
          name={`protocols.${index}.password` as const}
          label="密碼"
          required
          control={<Input.Password placeholder="協議密碼" />}
        />
      )}
      {type === 'hysteria2' && (
        <>
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.productionVisible` as const}
            label="下發給客戶端"
            help="明確控制 Hy2 發布；新版不要求 proof，仍須有效端點、認證及 Salamander 密碼。"
            valuePropName="checked"
            control={<Switch checkedChildren="下發" unCheckedChildren="暫停" />}
          />
          <Typography.Text strong>歷史 Production Proof（選填）</Typography.Text>
          <Typography.Paragraph type="secondary">
            保留既有紀錄供舊版相容與回復參考；缺少或過期不影響新版下發，也不代表目前連線健康。
          </Typography.Paragraph>
          {historicalProof ? (
            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 160, overflow: 'auto' }}>
              {JSON.stringify(historicalProof, null, 2)}
            </pre>
          ) : <Typography.Text type="secondary">沒有歷史紀錄</Typography.Text>}
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.obfs.password` as const}
            label="Salamander Obfs 密碼"
            control={<Input.Password placeholder="下發 Hy2 時必填 Salamander 密碼" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.server_ports` as const}
            label="Port Hopping"
            control={
              <Input.TextArea
                rows={2}
                placeholder={'每行一個 port 或 range，例如\n5443\n5444:5450'}
              />
            }
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.hop_interval` as const}
            label="Hop Interval"
            control={<Input placeholder="例如 30s，未填則使用 sing-box 預設" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.up_mbps` as const}
            label="上行 Mbps"
            control={<InputNumber min={1} max={100000} style={{ width: '100%' }} />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.down_mbps` as const}
            label="下行 Mbps"
            control={<InputNumber min={1} max={100000} style={{ width: '100%' }} />}
          />
        </>
      )}
      {type === 'vless-reality' && (
        <>
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.uuid` as const}
            label="UUID"
            required
            control={<Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.public_key` as const}
            label="Reality Public Key"
            required
            control={<Input placeholder="base64 public key" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.short_id` as const}
            label="Reality Short ID"
            required
            control={<Input placeholder="hex（可為空字串）" />}
          />
          {/*
            REALITY dest（Aparecium NewSessionTicket 偵測對策，2026-04-27）
            推薦使用 BoringSSL 系列伺服器（dl.google.com / *.gstatic.com / google.com），
            它們在 TLS 1.3 握手後不會回傳 NewSessionTicket，能規避 Aparecium 主動偵測。
            微軟 (microsoft.com) 屬 with-ticket 系列，新節點不應使用。
          */}
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.dest.server` as const}
            label="Reality dest 伺服器"
            help="REALITY 偽裝目標。推薦：dl.google.com / www.gstatic.com（BoringSSL，no-ticket）"
            control={<Input placeholder="dl.google.com" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.dest.port` as const}
            label="Reality dest 端口"
            control={<InputNumber min={1} max={65535} placeholder="443" style={{ width: '100%' }} />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.dest.type` as const}
            label="Reality dest 類型"
            help="no-ticket = BoringSSL（推薦，反 Aparecium）；with-ticket = legacy（不推薦新節點）"
            control={
              <Select
                allowClear
                placeholder="no-ticket（推薦）"
                options={[
                  { value: 'no-ticket', label: 'no-ticket（BoringSSL，推薦）' },
                  { value: 'with-ticket', label: 'with-ticket（legacy，不推薦）' },
                ]}
              />
            }
          />
        </>
      )}
      {type === 'amneziawg' && <AmneziaWGSubForm index={index} />}
    </div>
  );
};

// ---------- AmneziaWG sub-form ----------

interface AmneziaWGSubFormProps {
  index: number;
}

/**
 * AB-T2 / D3 — amneziaWG 9th outbound 編輯表單。
 *
 * 包含 9+ 個 nodeConfigJson.protocols[i] 欄位：privateKey、localAddresses、
 * peers 陣列（含 5 個 fields per peer）、junk 參數（count/minSize/maxSize）、
 * S1/S2 hex padding、H1..H4 32-bit magic headers、MTU。
 *
 * Genkey buttons 透過 admin endpoints `/api/admin/amneziawg/{genkey,genmagic}`
 * 一鍵產生 base64 X25519 keypair + per-node H1..H4 hex（用 crypto.randomBytes）。
 */
const AmneziaWGSubForm: React.FC<AmneziaWGSubFormProps> = ({ index }) => {
  const { control, setValue } = useFormContext<ProtocolsFormValues>();
  const peersFieldArray = useFieldArray<ProtocolsFormValues>({
    control,
    name: `protocols.${index}.peers` as const,
  });
  const [genkeyLoading, setGenkeyLoading] = useState(false);
  const [genmagicLoading, setGenmagicLoading] = useState(false);

  const handleGenKeypair = async () => {
    setGenkeyLoading(true);
    try {
      const res = await authFetch('/api/admin/amneziawg/genkey', {
        method: 'POST',
      });
      const result = await res.json();
      if (result.code === 0 && result.data) {
        // 把 private key 填入 form；public key 顯示給 operator 抄寫到 server peer
        setValue(`protocols.${index}.privateKey` as const, result.data.privateKey, {
          shouldDirty: true,
          shouldValidate: true,
        });
        // 同步把產生的 public key 填到第 0 個 peer（client side server peer 通常是第一個）
        if (peersFieldArray.fields.length > 0) {
          setValue(
            `protocols.${index}.peers.0.publicKey` as const,
            result.data.publicKey,
            { shouldDirty: true, shouldValidate: true }
          );
        }
        message.success('已產生 X25519 keypair（private/public 已填入表單）');
      } else {
        message.error(result.msg || '產生 keypair 失敗');
      }
    } catch (e) {
      message.error('產生 keypair 失敗');
    } finally {
      setGenkeyLoading(false);
    }
  };

  const handleGenMagic = async () => {
    setGenmagicLoading(true);
    try {
      const res = await authFetch('/api/admin/amneziawg/genmagic', {
        method: 'POST',
      });
      const result = await res.json();
      if (result.code === 0 && result.data) {
        setValue(`protocols.${index}.h1` as const, result.data.h1, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`protocols.${index}.h2` as const, result.data.h2, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`protocols.${index}.h3` as const, result.data.h3, {
          shouldDirty: true,
          shouldValidate: true,
        });
        setValue(`protocols.${index}.h4` as const, result.data.h4, {
          shouldDirty: true,
          shouldValidate: true,
        });
        message.success('已產生 H1..H4 random hex（32-bit each）');
      } else {
        message.error(result.msg || '產生 H1..H4 失敗');
      }
    } catch (e) {
      message.error('產生 H1..H4 失敗');
    } finally {
      setGenmagicLoading(false);
    }
  };

  return (
    <>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        AmneziaWG 是 WireGuard fork，加入 junk packet + S1/S2 padding + H1..H4
        magic header obfuscation。Operator 必須完成 client/server/libbox 三層部署
        後才能啟用，詳見 migration `1711756800057-AmneziaWGSchema.ts` checklist。
      </Typography.Paragraph>

      <Space style={{ marginBottom: 12 }} wrap>
        <Button
          icon={<ReloadOutlined />}
          loading={genkeyLoading}
          onClick={handleGenKeypair}
          size="small"
        >
          一鍵產生 WG keypair
        </Button>
        <Button
          icon={<ReloadOutlined />}
          loading={genmagicLoading}
          onClick={handleGenMagic}
          size="small"
        >
          一鍵產生 H1..H4
        </Button>
      </Space>

      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.privateKey` as const}
        label="Private Key"
        required
        help="base64 32-byte WG private key（44 chars）。每節點 per-client 獨立。"
        control={<Input.TextArea rows={2} placeholder="OPNQEdvxXFkDSjtD8gGVbTujeDEWWXQQohhedNvPzkg=" />}
      />

      <Controller
        control={control}
        name={`protocols.${index}.localAddresses` as const}
        render={({ field, fieldState }) => (
          <FormItemWrap label="Local Addresses (CIDR)" required help="每行一個。例：10.0.0.2/32" error={fieldState.error?.message}>
            <Input.TextArea
              rows={2}
              placeholder="10.0.0.2/32"
              value={Array.isArray(field.value) ? field.value.join('\n') : field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value)}
              onBlur={field.onBlur}
            />
          </FormItemWrap>
        )}
      />

      <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
        Peers
      </Typography.Text>
      {peersFieldArray.fields.map((peerField, peerIdx) => (
        <div
          key={peerField.id}
          style={{
            position: 'relative',
            padding: '8px 36px 4px 8px',
            border: '1px dashed var(--ant-color-border-secondary, #f0f0f0)',
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <Button
            type="text"
            size="small"
            danger
            disabled={peersFieldArray.fields.length <= 1}
            aria-label={`刪除第 ${peerIdx + 1} 個 peer`}
            icon={<DeleteOutlined />}
            style={{ position: 'absolute', top: 2, right: 2 }}
            onClick={() => peersFieldArray.remove(peerIdx)}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Peer #{peerIdx + 1}
          </Typography.Text>
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.peers.${peerIdx}.publicKey` as const}
            label="Public Key"
            required
            help="Server 端 WG static public key（base64 44 chars）"
            control={<Input.TextArea rows={2} placeholder="base64 public key" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.peers.${peerIdx}.presharedKey` as const}
            label="Preshared Key"
            help="選填：對稱 PSK（base64 44 chars），強烈建議啟用以增強安全"
            control={<Input.TextArea rows={2} placeholder="（選填）base64 PSK" />}
          />
          <Controller
            control={control}
            name={`protocols.${index}.peers.${peerIdx}.allowedIPs` as const}
            render={({ field, fieldState }) => (
              <FormItemWrap label="Allowed IPs" required help="每行一個 CIDR。Client 通常為 0.0.0.0/0 + ::/0" error={fieldState.error?.message}>
                <Input.TextArea
                  rows={2}
                  placeholder={'0.0.0.0/0\n::/0'}
                  value={Array.isArray(field.value) ? field.value.join('\n') : field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                />
              </FormItemWrap>
            )}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.peers.${peerIdx}.serverEndpoint` as const}
            label="Server Endpoint"
            help="hostname 或 IP，例：endpoint.swiftmesh.net"
            control={<Input placeholder="endpoint.swiftmesh.net" />}
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.peers.${peerIdx}.serverPort` as const}
            label="Server Port"
            control={
              <InputNumber min={1} max={65535} style={{ width: '100%' }} />
            }
          />
          <FormField<ProtocolsFormValues>
            name={`protocols.${index}.peers.${peerIdx}.persistentKeepaliveInterval` as const}
            label="Persistent Keepalive (秒)"
            help="0..65535，0 = 停用。手機運營商建議 25 秒"
            control={<InputNumber min={0} max={65535} style={{ width: '100%' }} />}
          />
        </div>
      ))}
      <Button
        size="small"
        icon={<PlusOutlined />}
        onClick={() =>
          peersFieldArray.append({
            publicKey: '',
            presharedKey: undefined,
            allowedIPs: ['0.0.0.0/0'],
            serverEndpoint: undefined,
            serverPort: undefined,
            persistentKeepaliveInterval: undefined,
          })
        }
        style={{ marginBottom: 12 }}
      >
        新增 Peer
      </Button>

      <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
        Junk Packet 參數（握手前送 N 個 random-data UDP）
      </Typography.Text>
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.junkCount` as const}
        label="Junk Count"
        help="0..256，0 = 停用。AmneziaVPN 建議 4..15 對應 Russia GFW 場景"
        control={<InputNumber min={0} max={256} style={{ width: '100%' }} />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.junkMinSize` as const}
        label="Junk Min Size (bytes)"
        help="16..2000"
        control={<InputNumber min={16} max={2000} style={{ width: '100%' }} />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.junkMaxSize` as const}
        label="Junk Max Size (bytes)"
        help="16..2000，必須 ≥ junkMinSize"
        control={<InputNumber min={16} max={2000} style={{ width: '100%' }} />}
      />

      <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
        S1 / S2 Junk Padding（hex byte sequences，≤ 256 bytes）
      </Typography.Text>
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.s1Hex` as const}
        label="S1 Hex"
        help="附加到 handshake init 訊息的 junk byte sequence。例：0a1b2c3d4e5f"
        control={<Input.TextArea rows={2} placeholder="（選填）hex string" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.s2Hex` as const}
        label="S2 Hex"
        help="附加到 handshake response 訊息的 junk byte sequence"
        control={<Input.TextArea rows={2} placeholder="（選填）hex string" />}
      />

      <Typography.Text strong style={{ display: 'block', marginTop: 8, marginBottom: 8 }}>
        H1..H4 Magic Header (32-bit hex per node)
      </Typography.Text>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        替換 WG 靜態 0x01/0x02/0x03/0x04 開頭。至少一個必須 ≠ stock value，否則
        fingerprint defense 等於 no-op。Operator 應用 `hkdf-derive.sh` 從 master_secret +
        node_id 產生（見 runbook §D3），或按上方「一鍵產生 H1..H4」隨機產生。
      </Typography.Paragraph>
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.h1` as const}
        label="H1"
        control={<Input placeholder="例：0xa1b2c3d4 或留空" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.h2` as const}
        label="H2"
        control={<Input placeholder="例：0x12345678 或留空" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.h3` as const}
        label="H3"
        control={<Input placeholder="例：0xdeadbeef 或留空" />}
      />
      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.h4` as const}
        label="H4"
        control={<Input placeholder="例：0xcafebabe 或留空" />}
      />

      <FormField<ProtocolsFormValues>
        name={`protocols.${index}.mtu` as const}
        label="MTU"
        help="1280..1500（預設 1280，IPv6 minimum，手機運營商相容）"
        control={<InputNumber min={1280} max={1500} style={{ width: '100%' }} />}
      />
    </>
  );
};

// ---------- Local helper for amneziawg multi-line CIDR fields ----------

interface FormItemWrapProps {
  label: ReactNode;
  required?: boolean;
  help?: ReactNode;
  error?: string;
  children: ReactNode;
}

/**
 * 輕量包裝：把 RHF Controller render 出來的 input 套上 AntD Form.Item 樣式
 * + 自動 errors 顯示。用於需要 Controller 直接控制 value/onChange 轉換的欄位
 * （例如 textarea string ↔ array CIDR）。
 */
const FormItemWrap: React.FC<FormItemWrapProps> = ({ label, required, help, error, children }) => (
  <Form.Item
    label={label}
    required={required}
    validateStatus={error ? 'error' : undefined}
    help={error || help}
  >
    {children}
  </Form.Item>
);
